/**
 * @module ServiceCheckForm
 * @description Service Check Form
 */
import { useContext, useState, useRef, useEffect } from "react";
import debounce from "lodash/debounce";
import { FuelContext } from "@/providers/fuel-provider";
import { usePredictiveSearch } from "@/hooks/usePredictiveSearch";
import { isValidAddressInput } from "@/js/utils/validators";
import { stringify } from "@/js/utils/filters";
import { trackEvent } from "@/js/helpers/tracking";
import state from "@/js/config/state";
import { v4 as uuidv4 } from "uuid";
import styles from "./index.module.css";

const formContext = {
  formType: "SERVICE CHECK - SINGLE LINE",
  formName: "SERVICE CHECK - SINGLE LINE",
};

/**
 * @function Component
 * @description
 */
const Component = ({ ...props }) => {
  const { isDark, isHero } = props;
  const { setSuggestionsVisible } = useContext(FuelContext);
  const [addressValid, setAddressValid] = useState(false);
  const [addressError, setAddressError] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [addressObject, setAddressObject] = useState({});
  const [formStarted, setFormStarted] = useState(false);
  const [addressKey, setAddressKey] = useState(false);
  const [placeholderText, setPlaceholderText] = useState("Enter your address.");
  const id = uuidv4();

  const addressField = useRef(null);
  const honeyPotField = useRef(null);

  const predictive = usePredictiveSearch(
    { addressField, honeyPotField },
    setSuggestions,
    formContext,
  );

  useEffect(() => {
    setSuggestionsVisible(suggestions.length > 0);
    return () => setSuggestionsVisible(false);
  }, [suggestions, setSuggestionsVisible]);

  /**
   * @function setSuggestionValue
   * @description Set suggestion value on address and zip
   */
  const setSuggestionValue = async (event) => {
    const { address, addressData } = await predictive.setSuggestionValue(event);

    setAddressValid(isValidAddressInput(address));
    setAddressError(!isValidAddressInput(address));
    setAddressObject(addressData);
    setAddressKey(true);
  };

  /**
   * @function fetchAddress
   * @description Fetch our address suggestions
   */
  const fetchAddress = async (event) => {
    if (!formStarted) {
      trackEvent({ action: "formStarted", data: formContext });
      setFormStarted(true);
    }

    predictive.fetchAddress(event);
  };

  /**
   * @function handleResize
   * @description Update placeholder text if viewport changes
   */
  const handleResize = () => {
    if (state.is_desktop) {
      return setPlaceholderText(
        "Enter your address to view plans in your area.",
      );
    }

    setPlaceholderText("Enter your address.");
  };

  /**
   * @function useEffect
   * @description Set our placeholder, then attach viewport resize event
   */
  useEffect(() => {
    handleResize();
    document.addEventListener("redventures/resize_executed", handleResize);
  }, []);

  return (
    <div
      className={`${styles.component} ${isDark ? styles.component__isDark : ""} ${isHero ? styles.component__isHero : ""}`}
    >
      <form
        name="service-check-form"
        className={styles.component__form}
        autoComplete="off"
        onSubmit={predictive.handleSubmit}
        data-address-object={stringify(addressObject)}
        data-cypress="service-check-form"
      >
        <span
          className={`${styles.component__icon} icon icon-location-marker-2`}
        />
        <div className={`field ${styles.component__formField}`}>
          <label
            className="screen-reader-text"
            htmlFor={`street-address-${id}`}
          >
            Street Address
          </label>
          <input
            ref={addressField}
            type="text"
            name="street-address"
            id={`street-address-${id}`}
            data-test-id="street-address"
            onInput={debounce(fetchAddress, 500) /* fetchAddress */}
            className="input"
            placeholder={placeholderText}
            data-payload={stringify({
              fieldName: "street",
              fieldType: "street",
              fieldValue: "STREET ADDRESS",
            })}
          />
          <span
            className={`field-error ${addressError === true ? "--active" : ""}`}
          >
            Invalid Address
          </span>
          <div
            className={`${styles.component__suggestions} ${suggestions && suggestions.length > 0 ? styles.component__suggestionsActive : ""}`}
          >
            {suggestions && suggestions.length > 0 && (
              <>
                <ul className={styles.component__suggestionsList}>
                  {suggestions.map((suggestion, index) => (
                    <li key={index}>
                      <button
                        type="button"
                        role="button"
                        data-address={suggestion.value}
                        data-zip={suggestion.zipCode}
                        data-object={stringify(suggestion)}
                        onClick={setSuggestionValue}
                      >
                        {suggestion.value}
                      </button>
                    </li>
                  ))}
                </ul>
                <div className={styles.component__manual}>
                  <button
                    type="button"
                    role="button"
                    onClick={() => setSuggestions([])}
                  >
                    Close address suggestions
                  </button>
                </div>
              </>
            )}
          </div>
          {/* <!-- .component__suggestions --> */}
        </div>
        {/* <!-- .field --> */}

        <div className="field" data-hidden-input>
          <label htmlFor={`hidden-input-${id}`}>Hidden Input</label>
          <input
            ref={honeyPotField}
            type="text"
            name="hidden-input"
            id={`hidden-input-${id}`}
            onInput={predictive.handleInput}
            data-payload={stringify({
              fieldName: "honeypot",
              fieldType: "honeypot",
              fieldValue: "HONEYPOT",
            })}
          />
        </div>
        {/* <!-- .field --> */}

        <button
          type="submit"
          className="btn"
          disabled={addressValid !== true || addressKey !== true}
          aria-disabled={addressValid !== true || addressKey !== true}
          role="button"
        >
          Check availability
        </button>
      </form>
    </div>
  );
};

export default Component;
